import Model from "./Model.js";

/**
 * Representation of a domain Account.
 *
 * @param {string} spec.fiscalYearId - Fiscal year id of the fiscal year
 * @param {string} spec.name - Name of the account
 * @param {string} spec.number - Account identifier
 * @param {number} spec.openingBalance - Opening balance value for the account
 */
export default class Account extends Model {
	constructor(spec = {}) {
		super(spec);
		this._fiscalYearId = spec.fiscalYearId;
		this._name = spec.name;
		this._number = spec.number;
		this._openingBalance = spec.openingBalance;
	}

	getId() {
		return this.getNumber();
	}

	getFiscalYearId() {
		return this._fiscalYearId;
	}

	getName() {
		return this._name;
	}

	getNumber() {
		return this._number;
	}

	getOpeningBalance() {
		return this._openingBalance;
	}

	isInInterval({ start, end }) {
		if (this._number.length < start.length) {
			return false;
		}

		return (
			start <= this._number.substring(0, start.length) &&
			end >= this._number.substring(0, end.length)
		);
	}

	/**
	 * Return `true` if the receiver's number belongs to `accountNumber`.
	 * Belonging means that the receiver number starts with `accountNumber`.
	 *
	 * @example
	 * - '1010' belongs to '101'
	 * - '42' belongs to '4'
	 */
	isSubAccountOfAccountNumber(accountNumber) {
		return this._number.startsWith(accountNumber);
	}

	compareTo(other) {
		return this.getNumber().localeCompare(other.getNumber());
	}

	toString() {
		return `${this.getNumber()} - ${this.getName()}`;
	}

	toLabel() {
		return this.toString();
	}

	_getMatchingFields() {
		return [this.getName(), this.getNumber()];
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.fiscalYearId = this._fiscalYearId;
		properties.name = this._name;
		properties.number = this._number;
		properties.openingBalance = this._openingBalance;

		return properties;
	}
}
